import { gql, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { Header } from '../components/Header';
import { TmsTaskCardItem } from '../components/TmsTaskCardItem';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsLoadingScanComponent, HandleTmsLoadingScanEventResult } from './components/TmsLoadingScanComponent';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { Button, Dialog, Toast } from 'antd-mobile';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import { TmsOperationSourceEnum } from '../../../enums/TmsEnum';
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import { parseException } from '../../../utils/exceptionUtils';
import { GET_TMS_UNLOADING_INFO_SUMMARY, GetTmsUnloadingInfoSummaryResponse } from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { Pda } from '../components/Pda';

const HANDLE_TMS_UNLOADING_SCAN_EVENT = gql`
  mutation HandleTmsUnloadingScanEvent($input: HandleTmsUnloadingScanEventInput!) {
    handleTmsUnloadingScanEvent(input: $input) {
      success
      parcelQuantity
      isBag
      message
      destinationSortingCode
    }
  }
`;

interface HandleTmsUnloadingScanEventResult {
  success: boolean;
  parcelQuantity?: number | null;
  isBag?: boolean | null;
  message?: string | null;
  destinationSortingCode?: string | null;
}

const PageBody = ({ taskId, taskWayPointId }: { taskId: number; taskWayPointId: number }) => {
  const [handleTmsUnloadingScanEvent] = useMutation<{
    handleTmsUnloadingScanEvent: HandleTmsUnloadingScanEventResult;
  }>(HANDLE_TMS_UNLOADING_SCAN_EVENT);

  // todo dev: 卸车扫描 handler 待后端接口确认后完善
  const handleScanEvent = async (barCode: string): Promise<HandleTmsLoadingScanEventResult> => {
    Toast.show({
      icon: 'loading',
      content: t('pda.common.loading') || 'Loading...',
      duration: 0,
      maskClickable: false,
    });

    try {
      const { data } = await handleTmsUnloadingScanEvent({
        variables: {
          input: {
            taskId,
            taskWayPointId,
            barCode,
            operationSource: TmsOperationSourceEnum.PDA,
          }
        },
        refetchQueries: [
          { query: GET_TMS_UNLOADING_INFO_SUMMARY, variables: { taskId, taskWayPointId } },
        ],
      });
      return data?.handleTmsUnloadingScanEvent || { success: false };
    } finally {
      Toast.clear();
    }
  };

  return (
    <TmsLoadingScanComponent
      value1Placeholder={t('pda.common.bagCodeTrackingNo') || 'Bag Code/Tracking No'}
      value1ColumnTitle={t('pda.common.bagPackage') || 'Bag/Package'}
      handleScanEvent={handleScanEvent}
      quantityLabel={t('pda.pages.shipOut.unloading') ?? '卸车'}
    />
  );
};

const UnloadingBagSummaryCard = ({
  taskId,
  taskWayPointId,
  onColClick,
}: {
  taskId: number;
  taskWayPointId: number;
  onColClick: (detailType: number) => void;
}) => {
  const { data } = useQuery<GetTmsUnloadingInfoSummaryResponse>(GET_TMS_UNLOADING_INFO_SUMMARY, {
    variables: { taskId, taskWayPointId },
  });

  const summary = data?.getTmsUnloadingInfoSummary;
  const expectedBagCount = summary?.expectedBagCount ?? 0;
  const actualBagCount = summary?.actualBagCount ?? 0;
  const unloadedBagCount = summary?.unloadedBagCount ?? 0;

  const cols = [
    { label: t('pda.pages.unloadingDetail.expectedBagCount') || '应卸包数', value: expectedBagCount, color: 'text-purple-600', detailType: 1 },
    { label: t('pda.pages.unloadingDetail.actualBagCount') || '实卸包数', value: actualBagCount, color: 'text-green-600', detailType: 2 },
    { label: t('pda.pages.unloadingDetail.unloadedBagCount') || '未卸包数', value: unloadedBagCount, color: 'text-red-600', detailType: 3 },
  ];

  return (
    <div className="bg-white rounded-lg p-3 shadow-sm">
      <div className="flex">
        {cols.map((col, idx) => (
          <div
            key={idx}
            className="flex-1 flex flex-col items-center gap-1 cursor-pointer active:opacity-60"
            onClick={() => onColClick(col.detailType)}
          >
            <span className="text-sm font-semibold text-black">{col.label}</span>
            <span className={`text-xl font-semibold ${col.color}`}>{col.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function TmsUnloadingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastViewportHeightRef = useRef<number>(window.visualViewport?.height || window.innerHeight);

  const state = (location.state as TmsPageState) || null;

  const taskId = state?.taskId ? parseInt(state.taskId) : NaN;
  const taskWayPointId = state?.taskWaypointId ? parseInt(state.taskWaypointId) : NaN;

  if (!state || isNaN(taskId) || isNaN(taskWayPointId)) {
    return null;
  }

  const { backRoute, ...taskData } = state;

  const currentWaypointSortingCode = taskData.task?.waypoints
    ?.find(wp => String(wp.id) === String(taskWayPointId))
    ?.waypointSortingCode || '-';

  const handleBack = () => {
    navigate(backRoute || '/pda/home');
  };

  const handleNextStep = async () => {
    try {
      const { data } = await apolloClient.query<GetTmsUnloadingInfoSummaryResponse>({
        query: GET_TMS_UNLOADING_INFO_SUMMARY,
        variables: { taskId, taskWayPointId },
        fetchPolicy: 'network-only',
      });
      const unloadedBagCount = data?.getTmsUnloadingInfoSummary?.unloadedBagCount ?? 0;
      if (unloadedBagCount === 0) {
        navigate(PdaPath.PDA_TMS_UNLOADING_INFO, { state: { backRoute, ...taskData } });
        return;
      }
      const handler = Dialog.show({
        title: t('pda.pages.unloading.unloadedBagsTitle') || '仍有未卸包牌',
        content: (
          <div className="text-sm font-semibold text-gray-700 space-y-1">
            <div>{t('pda.pages.unloading.currentStation') || '当前站'}：<span className="text-purple-600">{currentWaypointSortingCode}</span></div>
            <div>{t('pda.pages.unloading.unloadedBagsCount') || '当前站点仍有未卸包牌，数量'}：<span className="text-red-500">{unloadedBagCount}</span></div>
            <div>{t('pda.pages.unloading.checkUnloadedHint') || '请检查是否还有未卸车扫描的包牌或车内有遗漏包牌。'}</div>
            <div>{t('pda.pages.unloading.continueUnloadingQuestion') || '是否继续卸车操作？'}</div>
          </div>
        ),
        actions: [
          [
            {
              key: 'allUnloaded',
              text: t('pda.pages.unloading.allUnloaded') || '已卸载全部',
              style: { color: '#6b7280' },
              onClick: () => {
                handler.close();
                navigate(PdaPath.PDA_TMS_UNLOADING_INFO, { state: { backRoute, ...taskData } });
              },
            },
            {
              key: 'continue',
              text: t('pda.common.continueButton') || '继续',
              bold: true,
              style: { color: '#7c3aed' },
              onClick: () => {
                handler.close();
              },
            },
          ],
        ],
      });
    } catch (e) {
      Toast.show({ content: parseException(e).message, position: 'center', duration: 2000 });
    }
  };

  const handleBagDetailClick = (detailType: number) => {
    navigate(PdaPath.PDA_TMS_UNLOADING_BAG_DETAIL, {
      state: { backRoute: PdaPath.PDA_TMS_UNLOADING, parentBackRoute: backRoute, ...taskData, detailType },
    });
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto pb-20">
        <Header title={t('pda.pages.shipOut.unloading')} backRoute={backRoute} />
        <Pda/>
        <TmsWaypointStepIndicator currentStep={1} variant="unloading-scan" />
        {taskData?.task && <TmsTaskCardItem task={taskData.task} showButtons={false} />}
        <UnloadingBagSummaryCard taskId={taskId} taskWayPointId={taskWayPointId} onColClick={handleBagDetailClick} />
        <PageBody taskId={taskId} taskWayPointId={taskWayPointId} />
      </div>

      <div
        ref={buttonContainerRef}
        className={`${getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').className} transition-opacity duration-300`}
        style={{
          ...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').style,
          willChange: 'opacity'
        }}
      >
        <div className="flex gap-2">
          <Button block color="default" onClick={handleBack} style={{ flex: 1 }}>
            {t('pda.common.back') || 'back'}
          </Button>
          <Button block color="primary" onClick={() => { void handleNextStep(); }} style={{ flex: 1 }}>
            {t('pda.common.nextStep') || 'nextStep'}
          </Button>
        </div>
      </div>
    </div>
  );
}
